import Details from '@/components/Details/Details';
import Tip from '@/components/Tip/Tip';
import { ROUTE_RESOURCE_GROUPS } from '@/constants/routes';
import useFeature from '@/hooks/useFeature';
import usePureState from '@/hooks/usePureState';
import IEventType from '@/types/IEventType';
import IResourceGroup from '@/types/IResourceGroup';
import { Flex, FormControl, FormLabel, Link, Switch, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { FcRegisteredTrademark } from 'react-icons/fc';
import { Link as L } from 'react-router-dom';
import Select from 'react-select';

export interface ISectionResourcesProps {
	resourceGroups: IResourceGroup[];
	service: IEventType;
	onChange: (values: Partial<IEventType>) => void;
}

export default function SectionResourcesSuccess(props: ISectionResourcesProps) {
	const isSectionEnabled = useFeature('resources');
	const { t } = useTranslation();
	const { state, dispatch } = usePureState({ isEnabled: _computeInitialChecked(props.service) });
	const options = _generateOptions(props.resourceGroups);
	const defaultValue = _computeInitialValue(props.service, props.resourceGroups);

	function handleToggleChange(event: React.ChangeEvent<HTMLInputElement>) {
		if (event.target.checked) {
			return dispatch({ isEnabled: true });
		}

		dispatch({ isEnabled: false });

		props.onChange({
			resourceRules: { availableInGroups: [] },
		});
	}

	if (!isSectionEnabled) {
		return null;
	}

	return (
		<Details image={<FcRegisteredTrademark />} title={t('Resources')}>
			<Tip>
				{t(
					'Associate different elements to a reservation page, such as a stretcher, a table in a restaurant or a room. A reservation can only be made if there are resources available in that slot.'
				)}
			</Tip>

			<Flex mb="4" justifyContent="space-between" alignItems="center">
				<Text ml="1">{t('Enable resources for this booking page')}</Text>
				<Switch isChecked={state.isEnabled} onChange={handleToggleChange} size="sm" mr="2" />
			</Flex>

			<FormControl display={state.isEnabled ? 'block' : 'none'}>
				<FormLabel></FormLabel>
				<Select
					menuPosition="fixed"
					value={defaultValue}
					options={options}
					onChange={(newValue) => {
						if (!newValue) {
							return;
						}

						props.onChange({
							resourceRules: {
								availableInGroups: [
									{
										group: { id: newValue.value },
										min: 1,
									},
								],
							},
						});
					}}
				/>
			</FormControl>

			<Link as={L} to={ROUTE_RESOURCE_GROUPS} textAlign="center" color="blue.700" display="block" mt={4}>
				{t('Click here to manage resources')}
			</Link>
		</Details>
	);
}

function _generateOptions(resourceGroups: IResourceGroup[]): Array<{ label: string; value: string }> {
	return resourceGroups.map(({ id, name }) => ({ label: name, value: id }));
}

function _computeInitialValue(
	service: IEventType,
	resourceGroups: IResourceGroup[]
): { value: string; label: string } | null {
	if (!service.resourceRules || service.resourceRules.availableInGroups.length === 0) {
		return null;
	}

	const groupId = service.resourceRules?.availableInGroups[0].group.id;

	return {
		value: groupId as string,
		label: resourceGroups?.find((r) => r.id === groupId)?.name as string,
	};
}

function _computeInitialChecked(service: IEventType): boolean {
	if (!service.resourceRules || service.resourceRules.availableInGroups.length === 0) {
		return false;
	}
	return true;
}
